import { CaretRightIcon, CheckIcon, CpuIcon, ImageIcon, TargetIcon } from '@phosphor-icons/react';

import { useModels, type ModelInfo } from '@/api-models/models';
import { IconButton } from '@/ui/button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/ui/DropdownMenu';

import { IMAGE_ACCEPT } from '../images';

/** A model's list price per 1M tokens, in dollars. Rates are µUSD. */
const perMillionUsd = (microUsd: number) => `$${(microUsd / 1_000_000).toFixed(2)}`;

/** Compact input / cached input / output cost line for a model row. */
function modelCosts(m: ModelInfo): string {
  return `${perMillionUsd(m.input_micro_usd_per_m)} in · ${perMillionUsd(
    m.cached_input_micro_usd_per_m,
  )} cached · ${perMillionUsd(m.output_micro_usd_per_m)} out / 1M`;
}

/** A slash boxed in a rounded square — the composer's "more actions" affordance. */
function SlashBoxIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" strokeWidth="1.6" />
      <path d="M14.5 7.5 9.5 16.5" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

interface ComposerMenuProps {
  /** Hidden file input the "Attach images" item clicks; owned by `useImageAttachments`. */
  inputRef: React.RefObject<HTMLInputElement | null>;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** False for text-only models: the attach item stays visible but explains why it's off. */
  canAttach: boolean;
  /** Currently selected model id — `null` means the gateway default. */
  model: string | null;
  onSelectModel: (id: string) => void;
  /** Opens the start-a-goal form; absent where goals do not run (planning sessions). */
  onStartGoal?: () => void;
  disabled?: boolean;
}

/**
 * Single entry point for everything the composer can do besides typing: image
 * attachment and the session's model, the latter as a submenu so the model list
 * (with its per-1M pricing) doesn't crowd the composer itself.
 */
export function ComposerMenu({
  inputRef,
  onFileInputChange,
  canAttach,
  model,
  onSelectModel,
  onStartGoal,
  disabled,
}: ComposerMenuProps) {
  const { models, defaultModel, isLoading } = useModels();

  const effectiveId = model ?? defaultModel ?? null;
  const selected = models.find((m) => m.id === effectiveId) ?? null;

  return (
    <>
      <input ref={inputRef} type="file" accept={IMAGE_ACCEPT} multiple hidden onChange={onFileInputChange} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton size={9} disabled={disabled} aria-label="Composer actions" title="Composer actions">
            <SlashBoxIcon size={17} />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="top" className="min-w-52">
          <DropdownMenuItem
            disabled={!canAttach}
            // Radix would otherwise still be closing the menu when the picker opens, and
            // Chrome drops a file dialog opened from an unmounting element.
            onSelect={(e) => {
              e.preventDefault();
              inputRef.current?.click();
            }}
          >
            <ImageIcon size={15} />
            Attach images
            {!canAttach && <span className="ml-auto text-2xs text-fg-muted">Vision models only</span>}
          </DropdownMenuItem>
          {onStartGoal && (
            <DropdownMenuItem onSelect={onStartGoal}>
              <TargetIcon size={15} />
              Start a goal…
            </DropdownMenuItem>
          )}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <CpuIcon size={15} />
              Model
              <span className="ml-auto flex items-center gap-1 pl-3">
                <span className="max-w-36 truncate font-mono text-2xs text-fg-tertiary">
                  {isLoading ? 'Loading…' : (selected?.display_name ?? model ?? 'Default')}
                </span>
                <CaretRightIcon size={12} className="text-fg-muted" />
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-80 w-72 max-w-[calc(100vw-3rem)] overflow-y-auto">
              {models.length === 0 && (
                <span className="block px-2 py-1.5 text-xs text-fg-tertiary">
                  {isLoading ? 'Loading models…' : 'No models available'}
                </span>
              )}
              {models.map((m) => (
                <DropdownMenuItem key={m.id} className="h-auto py-1.5" onSelect={() => onSelectModel(m.id)}>
                  <CpuIcon size={15} className="shrink-0" />
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="truncate font-mono text-xs text-fg">{m.display_name}</span>
                    <span className="truncate text-2xs text-fg-tertiary">{modelCosts(m)}</span>
                  </span>
                  {m.id === effectiveId && <CheckIcon size={14} className="ml-auto shrink-0 text-brand-text" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
